import { useState, useEffect, lazy, Suspense } from 'react';
import type { ReverseCalculationResult, ReverseCalculationInput, SavedScenario } from '../../types/tax';
import { formatCurrency } from '../../utils/formatting';
import { copyToClipboard, formatQuickShareText } from '../../utils/export/clipboardUtils';
import { SocialShare } from '../ui/SocialShare';
import { createCalculationShareData } from '../../utils/seo/socialSharing';
import { TaxDataManager } from '../../utils/taxYear/TaxDataManager';
import { ConsultingCta } from '../contact';
import ExportIntentGate from '../export/ExportIntentGate';
import { trackExportAttempt } from '../../utils/analytics/intentTracking';

// Lazy load the export modal with heavy PDF libraries
const ExportModal = lazy(() => import('../export/ExportModal'));

const EXPORT_SOURCE = 'results_display';

interface ResultsDisplayProps {
  result: ReverseCalculationResult;
  onRecalculate: () => void;
  inputFrequency?: 'monthly' | 'yearly';
  onSaveScenario?: () => void;
  calculationInput?: ReverseCalculationInput;
  scenarios?: SavedScenario[];
}

export default function ResultsDisplay({ 
  result, 
  onRecalculate, 
  inputFrequency = 'monthly', 
  onSaveScenario,
  calculationInput,
  scenarios = []
}: ResultsDisplayProps) {
  const [showExportModal, setShowExportModal] = useState(false);
  const [showIntentGate, setShowIntentGate] = useState(false);
  const [quickCopyStatus, setQuickCopyStatus] = useState<string>('');
  const [taxYearDisplayName, setTaxYearDisplayName] = useState<string>('');

  useEffect(() => {
    const taxDataManager = TaxDataManager.getInstance();
    setTaxYearDisplayName(taxDataManager.getDisplayName());
    
    const handleTaxYearChange = () => {
      setTaxYearDisplayName(TaxDataManager.getInstance().getDisplayName());
    };
    
    taxDataManager.subscribe(handleTaxYearChange);
    return () => taxDataManager.unsubscribe(handleTaxYearChange);
  }, []);

  const effectiveTaxRate = ((result.totalDeductions / result.grossIncome) * 100);
  const marginalTaxRate = ((result.taxAfterRebatesAndCredits / result.taxableIncome) * 100) || 0;

  const handleQuickCopy = async () => {
    const text = formatQuickShareText(result.grossIncome, result.netIncome, inputFrequency);
    const success = await copyToClipboard(text);
    
    if (success) {
      setQuickCopyStatus('Copied!');
    } else {
      setQuickCopyStatus('Failed');
    }
    
    setTimeout(() => setQuickCopyStatus(''), 2000);
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden">
      <div className="bg-sa-green px-6 py-4">
        <h3 className="text-xl font-bold text-white">Calculation Results</h3>
        <p className="text-green-100 text-sm">Your required gross income breakdown</p>
      </div>

      <div className="p-6">
        {/* Main Result */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-slate-300 mb-1">Required Gross Income</p>
            
            {/* Show emphasized result based on input frequency */}
            {inputFrequency === 'monthly' ? (
              <>
                <p className="text-4xl font-bold text-sa-green mb-2">
                  {formatCurrency(result.grossIncome / 12)}
                  <span className="text-lg text-gray-600 dark:text-slate-400 ml-2">per month</span>
                </p>
                <p className="text-2xl text-gray-700 dark:text-slate-300 mb-2">
                  {formatCurrency(result.grossIncome)}
                  <span className="text-sm text-gray-600 dark:text-slate-400 ml-1">per year</span>
                </p>
              </>
            ) : (
              <>
                <p className="text-4xl font-bold text-sa-green mb-2">
                  {formatCurrency(result.grossIncome)}
                  <span className="text-lg text-gray-600 dark:text-slate-400 ml-2">per year</span>
                </p>
                <p className="text-2xl text-gray-700 dark:text-slate-300 mb-2">
                  {formatCurrency(result.grossIncome / 12)}
                  <span className="text-sm text-gray-600 dark:text-slate-400 ml-1">per month</span>
                </p>
              </>
            )}
            
            <p className="text-sm text-gray-600 dark:text-slate-400">
              To achieve net income of {formatCurrency(inputFrequency === 'monthly' ? result.netIncome / 12 : result.netIncome)} {inputFrequency === 'monthly' ? 'per month' : 'per year'}
            </p>
            {result.accuracy > 1 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                ±{formatCurrency(result.accuracy)} accuracy after {result.iterations} iterations
              </p>
            )}
          </div>
        </div>

        {/* Social Sharing */}
        <div className="mb-6">
          <SocialShare 
            data={createCalculationShareData(
              result.grossIncome,
              result.netIncome,
              inputFrequency === 'monthly'
            )}
            variant="compact"
            showLabel={true}
          />
        </div>

        {/* Breakdown Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Income Breakdown */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-charcoal dark:text-slate-100 border-b border-gray-200 dark:border-slate-700 pb-2">
              Income Breakdown
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-slate-400">Gross Income</span>
                <span className="font-medium text-charcoal dark:text-slate-100">{formatCurrency(inputFrequency === 'monthly' ? result.grossIncome / 12 : result.grossIncome)}</span>
              </div>
              {result.pensionContribution > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-slate-400 text-sm pl-4">Less: Pension/Retirement Contributions</span>
                  <span className="font-medium text-sa-green">-{formatCurrency(inputFrequency === 'monthly' ? result.pensionContribution / 12 : result.pensionContribution)}</span>
                </div>
              )}
              {result.travelAllowanceTaxableAmount > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-slate-400 text-sm pl-4">Plus: Travel Allowance/Fringe Benefits</span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">+{formatCurrency(inputFrequency === 'monthly' ? result.travelAllowanceTaxableAmount / 12 : result.travelAllowanceTaxableAmount)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-slate-400">Taxable Income</span>
                <span className="font-medium text-charcoal dark:text-slate-100">{formatCurrency(inputFrequency === 'monthly' ? result.taxableIncome / 12 : result.taxableIncome)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-slate-700">
                <span className="text-charcoal dark:text-slate-100 font-medium">Net Income</span>
                <span className="font-semibold text-sa-green">{formatCurrency(inputFrequency === 'monthly' ? result.netIncome / 12 : result.netIncome)}</span>
              </div>
            </div>
          </div>

          {/* Deductions Breakdown */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-charcoal dark:text-slate-100 border-b border-gray-200 dark:border-slate-700 pb-2">
              Deductions
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-slate-400">Income Tax (before rebates)</span>
                <span className="font-medium text-charcoal dark:text-slate-100">
                  {formatCurrency(inputFrequency === 'monthly' ? result.taxBeforeRebates / 12 : result.taxBeforeRebates)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-slate-400 text-sm pl-4">Less: Tax Rebates</span>
                <span className="font-medium text-sa-green">
                  -{formatCurrency(inputFrequency === 'monthly' ? result.taxRebates / 12 : result.taxRebates)}
                </span>
              </div>
              {result.medicalAidCredit > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-slate-400 text-sm pl-4">Less: Medical Aid Credits</span>
                  <span className="font-medium text-sa-green">
                    -{formatCurrency(inputFrequency === 'monthly' ? result.medicalAidCredit / 12 : result.medicalAidCredit)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-slate-400">Income Tax (after rebates & credits)</span>
                <span className="font-medium text-charcoal dark:text-slate-100">
                  {formatCurrency(inputFrequency === 'monthly' ? result.taxAfterRebatesAndCredits / 12 : result.taxAfterRebatesAndCredits)}
                </span>
              </div>
              {result.pensionContribution > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-slate-400">Pension Contribution</span>
                  <span className="font-medium text-charcoal dark:text-slate-100">
                    {formatCurrency(inputFrequency === 'monthly' ? result.pensionContribution / 12 : result.pensionContribution)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-slate-400">UIF Contribution</span>
                <span className="font-medium text-charcoal dark:text-slate-100">
                  {formatCurrency(inputFrequency === 'monthly' ? result.uifContribution / 12 : result.uifContribution)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-slate-700">
                <span className="text-charcoal dark:text-slate-100 font-medium">Total Deductions</span>
                <span className="font-semibold text-error">
                  {formatCurrency(inputFrequency === 'monthly' ? result.totalDeductions / 12 : result.totalDeductions)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tax Rate Summary */}
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 mb-6">
          <h4 className="text-md font-semibold text-charcoal dark:text-slate-100 mb-3">Tax Rate Summary</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-700 dark:text-slate-200">{effectiveTaxRate.toFixed(1)}%</p>
              <p className="text-sm text-gray-600 dark:text-slate-400">Effective Tax Rate</p>
              <p className="text-xs text-gray-500 dark:text-slate-500">Total deductions as % of gross income</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-700 dark:text-slate-200">{marginalTaxRate.toFixed(1)}%</p>
              <p className="text-sm text-gray-600 dark:text-slate-400">Income Tax Rate</p>
              <p className="text-xs text-gray-500 dark:text-slate-500">Tax after rebates as % of taxable income</p>
            </div>
          </div>
        </div>

        {/* Monthly Breakdown */}
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <h4 className="text-md font-semibold text-charcoal dark:text-slate-100 mb-3">Monthly Breakdown</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-lg font-bold text-blue-700 dark:text-blue-400">{formatCurrency(result.grossIncome / 12)}</p>
              <p className="text-sm text-gray-600 dark:text-slate-400">Gross Monthly</p>
            </div>
            <div>
              <p className="text-lg font-bold text-error">{formatCurrency(result.totalDeductions / 12)}</p>
              <p className="text-sm text-gray-600 dark:text-slate-400">Monthly Deductions</p>
            </div>
            <div>
              <p className="text-lg font-bold text-green-700 dark:text-green-400">{formatCurrency(result.netIncome / 12)}</p>
              <p className="text-sm text-gray-600 dark:text-slate-400">Net Monthly</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Primary Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onRecalculate}
              className="btn-primary flex-1"
            >
              Calculate Different Amount
            </button>
            {onSaveScenario && (
              <button
                onClick={onSaveScenario}
                className="btn-primary sm:w-auto"
              >
                Save Scenario
              </button>
            )}
          </div>
          
          {/* Export Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                trackExportAttempt(EXPORT_SOURCE);
                setShowIntentGate(true);
              }}
              className="btn-secondary flex-1 flex items-center justify-center space-x-2"
            >
              <span>📤</span>
              <span>Export & Share</span>
            </button>
            <button
              onClick={handleQuickCopy}
              className="btn-secondary sm:w-auto flex items-center justify-center space-x-2 relative"
              disabled={!!quickCopyStatus}
            >
              <span>📋</span>
              <span>{quickCopyStatus || 'Quick Copy'}</span>
              {quickCopyStatus === 'Copied!' && (
                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                  Copied to clipboard!
                </span>
              )}
            </button>
            <button
              onClick={() => window.print()}
              className="btn-secondary sm:w-auto flex items-center justify-center space-x-2"
            >
              <span>🖨️</span>
              <span>Print</span>
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300">Important Disclaimer</h4>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                This calculation is based on PAYE, UIF deductions, medical aid tax credits, and pension contributions{taxYearDisplayName ? ` for the ${taxYearDisplayName} tax year` : ''}. 
                Calculations may be off by a few Rand on typical amounts, and potentially a few hundred Rand on large values. 
                Results do not include other potential deductions or personal circumstances. 
                Always consult with a qualified tax professional for personalized tax advice.
              </p>
            </div>
          </div>
        </div>

        {/* Consulting CTA — email capture (does not gate the result above) */}
        <ConsultingCta />
      </div>

      {/* Intent gate — fires export_attempt on click and gate_seen on show */}
      <ExportIntentGate
        isOpen={showIntentGate}
        source={EXPORT_SOURCE}
        onClose={() => setShowIntentGate(false)}
        onProceed={() => {
          setShowIntentGate(false);
          setShowExportModal(true);
        }}
        calculationContext={
          calculationInput
            ? {
                desiredNetIncome: calculationInput.desiredNetIncome,
                grossIncome: result.grossIncome,
                netIncome: result.netIncome,
                inputFrequency,
                taxYear: taxYearDisplayName || undefined,
              }
            : undefined
        }
      />

      {/* Export Modal */}
      {calculationInput && showExportModal && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-8 max-w-md">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-full"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        }>
          <ExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            result={result}
            input={calculationInput}
            inputFrequency={inputFrequency}
            scenarios={scenarios}
            title="SA Tax Calculation Results"
          />
        </Suspense>
      )}
    </div>
  );
}